import React from 'react';
import { Product } from 'types/Product';
import Show from 'components/Functional/Show';
import { PDPMetatags } from '../ProductDetails/PDPMetaTags';
import { isNotServerSideRendering } from 'helpers/env';
import { formatPrice } from 'helpers/currency';
import * as styles from './styles.module.scss';
import { useProductPrice } from '../../hooks/useProductPrice';
import cx from 'classix';
import { ValueBullets } from './ValueBullets';
import { PriceObject } from '../ProductDetails/ProductPrice';
import { DiscountPercentage } from 'components/DiscountPercentage';

type ProductHeaderProps = {
  product: Product;
  productTitle: string;
  siteUrl: string;
};

export const ProductHeaderMobileTest = ({ product, productTitle, siteUrl }: ProductHeaderProps): JSX.Element => {
  const price = useProductPrice();

  const isNotSSR = isNotServerSideRendering();

  const shouldDisplayDiscount = price.promoCode?.length > 0;
  const discount = getDiscountPercentage(price);

  const hasStrikethroughPrice = !!price.strikethroughPrice;

  return (
    <div className={styles.container}>
      <PDPMetatags product={product} siteUrl={siteUrl || ''} />

      <h1 data-testid="product-title">{productTitle}</h1>

      <div className={styles.priceRow}>
        {/* Initial price from SSR may be different from actual price due to product options, */}
        {/* so we can't display price until CSR kicks in */}
        <Show when={isNotSSR}>
          <Show when={hasStrikethroughPrice}>
            <span className={styles.strikethrough}>
              From {formatPrice(price.strikethroughPrice, { simplifyDigits: true })}
            </span>
          </Show>

          <span className={cx(styles.originalPrice, hasStrikethroughPrice && styles.strikethrough)}>
            {!hasStrikethroughPrice && 'From'} {formatPrice(price.productPriceTotal, { simplifyDigits: true })}
          </span>
        </Show>

        {shouldDisplayDiscount && <DiscountPercentage discount={discount} />}
      </div>

      <ValueBullets />
    </div>
  );
};

const getDiscountPercentage = (price: PriceObject) => {
  if (!price.strikethroughPrice || !price.productPriceTotal) {
    return null;
  }

  return (price.productPriceTotal - price.strikethroughPrice) / price.productPriceTotal * 100;
};
